import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Code,
  Star,
  Users,
  Calendar,
  Smartphone,
  Gamepad2,
  Rocket,
  Workflow,
  Brain,
  Globe,
  Server,
  Puzzle,
} from "lucide-react";
import config from "../config";

const iconMap: Record<string, React.ElementType> = {
  smartphone: Smartphone,
  "gamepad-2": Gamepad2,
  rocket: Rocket,
  workflow: Workflow,
  brain: Brain,
  globe: Globe,
  server: Server,
  puzzle: Puzzle,
};

const stats = [
  { value: "12+", label: "Years Experience", icon: Calendar },
  { value: "74+", label: "Projects Completed", icon: Code },
  { value: "4.6", label: "Avg Rating", icon: Star },
  { value: "50+", label: "Happy Clients", icon: Users },
];

const reasons = [
  {
    title: "Experienced Team",
    description:
      "A dedicated team delivering quality software solutions since 2012.",
    icon: Calendar,
  },
  {
    title: "End-to-End Solutions",
    description:
      "From concept and design to development, deployment, and ongoing support.",
    icon: Code,
  },
  {
    title: "Transparent Pricing",
    description:
      "Clear, upfront pricing with no hidden fees. You know exactly what you pay for.",
    icon: CheckCircle,
  },
  {
    title: "Proven Track Record",
    description:
      "Dozens of successful projects across mobile, web, gaming, and AI.",
    icon: Star,
  },
];

export default function Home() {
  const services = config.services
    .filter((s) => s.id !== "consultancy")
    .slice(0, 8);

  return (
    <div className="pt-[72px]">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-950 py-32 lg:py-44">
        {/* Animated gradient backdrop */}
        <div className="animate-gradient-bg absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-950 opacity-80" />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-cyan-400">
            Est. {config.company.foundedYear}
          </p>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            {config.company.tagline}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-400">
            {config.company.description}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-cyan-500/20 transition-transform hover:scale-105"
            >
              Our Services <ArrowRight size={20} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-700 px-8 py-4 text-base font-semibold text-slate-200 transition-colors hover:border-cyan-500 hover:text-cyan-400"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────── */}
      <section className="border-y border-white/5 bg-slate-900/50 py-12">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-8 px-6 md:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="flex flex-col items-center gap-2 text-center">
                <Icon className="text-cyan-400" size={28} />
                <span className="text-3xl font-extrabold text-white">
                  {stat.value}
                </span>
                <span className="text-sm text-slate-400">{stat.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Services Overview ────────────────────────────── */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-center text-3xl font-bold text-white sm:text-4xl">
            What We Do
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-slate-400">
            Comprehensive software development services tailored to your needs
          </p>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Code;
              return (
                <div
                  key={service.id}
                  className="group rounded-2xl border border-white/5 bg-slate-900/60 p-8 transition-all hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/5"
                >
                  <Icon
                    className="mb-4 text-cyan-400 transition-transform group-hover:scale-110"
                    size={36}
                  />
                  <h3 className="text-lg font-semibold text-white">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 px-7 py-3 font-semibold text-white shadow-md transition-transform hover:scale-105"
            >
              View All Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ────────────────────────────────── */}
      <section className="border-t border-white/5 bg-slate-900/30 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-center text-3xl font-bold text-white sm:text-4xl">
            Why Choose Us
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-slate-400">
            Trusted by clients worldwide for reliable, high-quality software
          </p>

          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((reason) => {
              const Icon = reason.icon;
              return (
                <div
                  key={reason.title}
                  className="rounded-2xl border border-white/5 bg-slate-900/60 p-8"
                >
                  <Icon className="mb-4 text-purple-400" size={32} />
                  <h3 className="text-lg font-semibold text-white">
                    {reason.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────── */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to Build Something Amazing?
          </h2>
          <p className="mt-4 text-lg text-slate-400">
            Let&rsquo;s discuss your project and turn your ideas into reality.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 px-10 py-4 text-lg font-semibold text-white shadow-lg shadow-cyan-500/20 transition-transform hover:scale-105"
          >
            Get Started <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
